import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react';

const ModalContext = createContext();

export function useModal() {
  return useContext(ModalContext);
}

export function ModalProvider({ children }) {
  const [modal, setModal] = useState(null);
  const [promptValue, setPromptValue] = useState('');

  const close = useCallback(() => setModal(null), []);

  const success = useCallback((msg) => setModal({ type: 'success', message: msg }), []);
  const error = useCallback((msg) => setModal({ type: 'error', message: msg }), []);
  const info = useCallback((msg) => setModal({ type: 'info', message: msg }), []);
  const confirm = useCallback((msg, onConfirm) => setModal({ type: 'confirm', message: msg, onConfirm }), []);
  // prompt: pide un dato al usuario. opts = { defaultValue, inputType, label, placeholder }.
  // onConfirm recibe el valor introducido (string).
  const prompt = useCallback((msg, opts, onConfirm) => {
    setPromptValue((opts && opts.defaultValue) || '');
    setModal({
      type: 'prompt',
      message: msg,
      onConfirm,
      inputType: (opts && opts.inputType) || 'text',
      label: opts && opts.label,
      placeholder: opts && opts.placeholder,
    });
  }, []);

  useEffect(() => {
    if (!modal) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'Enter' && modal.type !== 'confirm' && modal.type !== 'prompt') close();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [modal, close]);

  // Auto-cierre solo de modales 'success' (confirmaciones cortas de
  // operaciones realizadas correctamente). 'error', 'info', 'confirm' y
  // 'prompt' exigen acción del usuario y nunca se cierran solos.
  useEffect(() => {
    if (modal?.type !== 'success') return;
    const t = setTimeout(close, 2000);
    return () => clearTimeout(t);
  }, [modal, close]);

  const icons = {
    success: <CheckCircle size={44} color="#22c55e" />,
    error: <XCircle size={44} color="#ef4444" />,
    info: <Info size={44} color="var(--primary)" />,
    confirm: <AlertTriangle size={44} color="#f59e0b" />,
    prompt: <AlertTriangle size={44} color="#f59e0b" />,
  };

  return (
    <ModalContext.Provider value={{ success, error, info, confirm, prompt }}>
      {children}
      {modal && (
        <div className="modal-overlay" onClick={close}>
          <div className="app-modal" onClick={e => e.stopPropagation()}>
            <button className="app-modal-close" onClick={close} aria-label="Cerrar">
              <X size={18} />
            </button>
            <div className="app-modal-icon">{icons[modal.type]}</div>
            <p className="app-modal-message">{modal.message}</p>
            {modal.type === 'prompt' && (
              <div style={{ margin: '0 0 16px', textAlign: 'left' }}>
                {modal.label && (
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#475569' }}>{modal.label}</label>
                )}
                <input
                  className="form-input"
                  type={modal.inputType}
                  value={promptValue}
                  placeholder={modal.placeholder}
                  autoFocus
                  onChange={e => setPromptValue(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
            )}
            <div className="app-modal-actions">
              {(modal.type === 'confirm' || modal.type === 'prompt') ? (
                <>
                  <button className="btn btn-outline" onClick={close}>Cancelar</button>
                  <button className="btn btn-primary" onClick={() => { modal.onConfirm?.(modal.type === 'prompt' ? promptValue : undefined); close(); }}>Aceptar</button>
                </>
              ) : (
                <button className="btn btn-primary" onClick={close}>Aceptar</button>
              )}
            </div>
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
}
